// Conversation.Elicitation — the typed clarifying-question round-trip,
// as a Field-carrying FORM.
//
// The agent composes a short typed form on the fly (`ElicitationRequest`:
// a title + optional note + a non-empty list of `ElicitationStep`s, each a
// non-empty group of `Field`s — a flat form is the single-step case); the
// Author answers it (`ElicitationAnswer`: a `FieldAnswer` per field, flat
// across all steps — stepping is a render concern, the answer is one
// submission). A distinct
// request/answer *pair* (never one flagged event) so the round-trip is
// replayable in the log — the answer pairs back to its request by the
// agent message's id (see `artifact.ts` `InformationAnswer.requestMessageId`).
//
// **The Field algebra (LANE S3 — the durable half of the TurnEpilogue).**
// This is the domain twin of the UI's `ChatField` sum
// (`packages/client/src/components/Chat/types.ts`), which was built ahead of
// the domain. `field_multi_choice` is the general case that makes the
// agent-invented binary combiner ("Båda") STRUCTURALLY unrepresentable:
// selecting two options = "both", selecting all = "all", so there is no
// combiner value to construct. A free-text escape ("Annat…") is a
// companion optional `field_text`, NOT a sentinel "other" option — keeping
// it a distinct field keeps the option set closed and the escape typed
// (make-illegal-states-unrepresentable). Opt-out is the always-available
// Skip (UI `dismissed` status), which submits nothing.
//
// **No field is required (D47).** A form is HELP, not a demand: every question
// can be left blank and the form still submits. Older stored forms carry a
// `required` flag on some fields; it is ignored (the fold trusts payloads, and
// nothing reads it any more).
//
// **Source picks (D42).** `field_sources` asks the Author to pick library
// sources. The field carries NO options: the client offers the library's own
// sources, so the agent can never send a stale or partial list. Its answer
// (`answer_sources`) snapshots each pick's id AND title, so the agent reads
// what was chosen even if a source is later renamed or removed.
//
// **Correlation invariant.** `FieldKey` is branded because it is the
// load-bearing correlation between a field and its answer: a `FieldAnswer`
// references the `key` of the field it satisfies. Human-facing text
// (title, label, help, option labels) is plain string — it is display
// content the agent authored, not an invariant the domain enforces.
//
// **MVP shape — flagged.** `decideSupplyInformation` checks only that the
// answer pairs back to a real request marker; it does NOT yet validate
// that each answer's `key` names a field in the request, nor that a
// multi-choice answer's values are a subset of that field's options.
// Those are additive decide-time checks (the shape already carries what
// they need) — deferred, not designed out.
export { mkChoiceOption, mkElicitationAnswer, mkElicitationRequest, mkElicitationStep, mkFieldKey, mkMultiChoiceAnswer, mkMultiChoiceField, mkScaleAnswer, mkScaleField, mkScaleLevels, mkSingleChoiceAnswer, mkSingleChoiceField, mkSourcePick, mkSourcesAnswer, mkSourcesField, mkStepKey, mkSteppedRequest, mkTextAnswer, mkTextField, };
const mkFieldKey = (s) => s;
const mkStepKey = (s) => s;
const mkChoiceOption = (value, label, help) => help === undefined ? { value, label } : { value, label, help };
const mkTextField = (key, label, opts) => ({
    type: "field_text",
    key,
    label,
    ...(opts?.help === undefined ? {} : { help: opts.help }),
    ...(opts?.placeholder === undefined
        ? {}
        : { placeholder: opts.placeholder }),
});
const mkSingleChoiceField = (key, label, options, help) => ({
    type: "field_single_choice",
    key,
    label,
    options,
    ...(help === undefined ? {} : { help }),
});
const mkMultiChoiceField = (key, label, options, help) => ({
    type: "field_multi_choice",
    key,
    label,
    options,
    ...(help === undefined ? {} : { help }),
});
// The levels for a `min`..`max` scale, or `undefined` unless there is
// exactly one per value — a partial list would name the wrong values.
const mkScaleLevels = (min, max, levels) => levels.length === max - min + 1 && levels.length > 0 ? levels : undefined;
const mkScaleField = (key, label, min, max, opts) => ({
    type: "field_scale",
    key,
    label,
    min,
    max,
    ...(opts?.help === undefined ? {} : { help: opts.help }),
    ...(opts?.minLabel === undefined ? {} : { minLabel: opts.minLabel }),
    ...(opts?.maxLabel === undefined ? {} : { maxLabel: opts.maxLabel }),
    ...(opts?.levels === undefined ? {} : { levels: opts.levels }),
});
const mkSourcesField = (key, label, help) => ({
    type: "field_sources",
    key,
    label,
    ...(help === undefined ? {} : { help }),
});
const mkElicitationStep = (key, fields, opts) => ({
    key,
    fields,
    ...(opts?.title === undefined ? {} : { title: opts.title }),
    ...(opts?.note === undefined ? {} : { note: opts.note }),
});
// Today's flat form == one anonymous step. Keeps every existing caller/fixture
// valid (the single-step degenerate case).
const mkElicitationRequest = (title, fields, note) => ({
    type: "elicitation_request",
    title,
    steps: [mkElicitationStep(mkStepKey("step"), fields)],
    ...(note === undefined ? {} : { note }),
});
// The stepped form: an explicit ordered list of steps.
const mkSteppedRequest = (title, steps, note) => ({
    type: "elicitation_request",
    title,
    steps,
    ...(note === undefined ? {} : { note }),
});
const mkTextAnswer = (key, value) => ({
    type: "answer_text",
    key,
    value,
});
const mkSingleChoiceAnswer = (key, value) => ({ type: "answer_single_choice", key, value });
const mkMultiChoiceAnswer = (key, values) => ({ type: "answer_multi_choice", key, values });
const mkScaleAnswer = (key, value) => ({
    type: "answer_scale",
    key,
    value,
});
const mkSourcePick = (sourceId, title) => ({
    sourceId,
    title,
});
const mkSourcesAnswer = (key, sources) => ({ type: "answer_sources", key, sources });
const mkElicitationAnswer = (answers) => ({ type: "elicitation_answer", answers });
//# sourceMappingURL=elicitation.js.map